import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { deviceId } from '../utils/deviceId';
import { tgInitData } from '../utils/telegram';
import { 
  AuthResponse, 
  LoginRequest, 
  RegisterRequest, 
  Route, 
  RouteCreate, 
  RouteUpdate, 
  UserSettings,
  JournalEntry,
  Credential,
  CredentialCreate,
  VenuePreset,
  VenuePresetUpdate,
  PresetPreview
} from '../types';

/** detail ошибки API → человеческая строка. 422 FastAPI: «поле: причина». */
function formatDetail(detail: unknown): string {
  if (Array.isArray(detail)) {
    return detail.map((e: any) => {
      if (e && typeof e === 'object') {
        const loc = Array.isArray(e.loc) ? e.loc.filter((x: unknown) => x !== 'body').join('.') : '';
        return `${loc ? loc + ': ' : ''}${e.msg ?? JSON.stringify(e)}`;
      }
      return String(e);
    }).join('; ');
  }
  if (detail && typeof detail === 'object') {
    const o = detail as any;
    return String(o.msg ?? o.message ?? JSON.stringify(o));
  }
  return String(detail);
}

/** Ключ запомненного устройства (см. backend auth.remember_device). Лежит
 *  отдельно от access-токена и при выходе не стирается: «Выйти» — это конец
 *  сессии, а не «забыть этот браузер» (для этого — кнопка в профиле). */
const DEVICE_KEY = 'sb_device_token';

class ApiClient {
  private api: AxiosInstance;
  /** Один обмен ключа устройства на всех: на 401 падает пачка запросов разом. */
  private refreshing: Promise<boolean> | null = null;
  private baseURL: string;

  /** Underlying axios instance for ad-hoc endpoints not yet wrapped with a typed method. */
  get raw(): AxiosInstance { return this.api; }

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL ?? '';
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Язык интерфейса — серверу: по нему он переводит свои тексты
        // (ошибки, причины в журнале сессий). См. backend_v2/i18n.py.
        try {
          const lang = localStorage.getItem('lang');
          if (lang) config.headers['X-Lang'] = lang;
        } catch { /* нет доступа к хранилищу */ }
        // Идентификатор браузера: по нему сервер ведёт список сессий и
        // блокирует устройства (utils/deviceId.ts).
        config.headers['X-Device-Id'] = deviceId();
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // FastAPI на ошибке валидации (422) кладёт в detail СПИСОК объектов
        // {type, loc, msg, input, ctx}. Больше тридцати мест выводят detail
        // как текст (toast/JSX) — и React падал с ошибкой #31 («объект как
        // ребёнок»). Приводим к строке здесь, один раз для всех.
        const d = error.response?.data;
        if (d && d.detail != null && typeof d.detail !== 'string') {
          d.detail = formatDetail(d.detail);
        }
        // /auth/* исключены: 401 на логине = неверный пароль, редирект
        // перезагружал страницу логина и съедал сообщение об ошибке
        const url: string = error.config?.url || '';
        if (error.response?.status === 401 && !url.startsWith('/auth/')) {
          // Сначала пробуем молча обновить вход ключом устройства и повторить
          // запрос. На страницу входа — только если обновить нечем или ключ
          // не приняли: раньше любой 401 стирал вход и требовал пароль и 2FA.
          const cfg: any = error.config;
          if (cfg && !cfg._sbRetried && this.getDeviceToken()) {
            cfg._sbRetried = true;
            return this.refreshByDevice().then((ok) => {
              if (ok) {
                cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${this.getToken()}` };
                return this.api.request(cfg);
              }
              this.clearAuth();
              window.location.href = '/login';
              return Promise.reject(error);
            });
          }
          this.clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', {
      ...credentials,
      device_token: this.getDeviceToken() || undefined,
      // Вход паролем внутри Telegram Mini App запоминает этот Telegram за
      // аккаунтом — в следующий раз вход будет сам.
      tg_init_data: tgInitData() || undefined,
    });
    // С включённой 2FA токена ещё нет — сначала код (verify2fa).
    if (response.data.access_token) this.setAuth(response.data.access_token);
    return response.data;
  }

  /** Второй шаг входа: код из Google Authenticator по challenge. */
  async verify2fa(challenge: string, code: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/2fa/verify', { challenge, code });
    if (response.data.access_token) this.setAuth(response.data.access_token);
    if (response.data.device_token) {
      try { localStorage.setItem(DEVICE_KEY, response.data.device_token); } catch { /* нет хранилища */ }
    }
    return response.data;
  }

  getDeviceToken(): string | null {
    try { return localStorage.getItem(DEVICE_KEY); } catch { return null; }
  }

  forgetDevice(): void {
    try { localStorage.removeItem(DEVICE_KEY); } catch { /* нет хранилища */ }
  }

  /** Обменять ключ устройства на новый access-токен. false — ключа нет или
   *  сервер его не принял (тогда ключ стираем: он уже бесполезен). */
  refreshByDevice(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    const dev = this.getDeviceToken();
    if (!dev) return Promise.resolve(false);
    this.refreshing = this.api.post('/auth/device/refresh', { device_token: dev })
      .then((r) => {
        if (!r.data?.access_token) return false;
        this.setAuth(r.data.access_token);
        return true;
      })
      .catch((e) => {
        const st = e?.response?.status;
        if (st === 401 || st === 403) this.forgetDevice();
        return false;
      })
      .finally(() => { this.refreshing = null; });
    return this.refreshing;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/register', userData);
    if (response.data.access_token) this.setAuth(response.data.access_token);
    return response.data;
  }

  setAuth(token: string): void {
    localStorage.setItem('access_token', token);
  }

  clearAuth(): void {
    localStorage.removeItem('access_token');
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Route management
  async getRoutes(includeInactive = false): Promise<Route[]> {
    const response: AxiosResponse<Route[]> = await this.api.get('/routes', {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  }

  async createRoute(routeData: RouteCreate): Promise<{ id: number; name: string; status: string }> {
    const response = await this.api.post('/routes', routeData);
    return response.data;
  }

  async getRoute(routeId: number): Promise<Route> {
    const response: AxiosResponse<Route> = await this.api.get(`/routes/${routeId}`);
    return response.data;
  }

  async updateRoute(routeId: number, updates: RouteUpdate): Promise<{ status: string }> {
    const response = await this.api.put(`/routes/${routeId}`, updates);
    return response.data;
  }

  // ---- пресеты связок ----
  async listPresets(): Promise<VenuePreset[]> {
    const response = await this.api.get('/presets');
    return response.data;
  }

  async createPreset(data: Partial<VenuePreset>): Promise<{ id: number; status: string }> {
    const response = await this.api.post('/presets', data);
    return response.data;
  }

  async updatePreset(id: number, updates: VenuePresetUpdate): Promise<{ status: string }> {
    const response = await this.api.put(`/presets/${id}`, updates);
    return response.data;
  }

  async deletePreset(id: number, stopSessions = true): Promise<{ status: string; stopped_sessions: number[]; kept_sessions?: number[] }> {
    const response = await this.api.delete(`/presets/${id}`, {
      params: { stop_sessions: stopSessions },
    });
    return response.data;
  }

  /** Что пресет поймал бы на текущем снимке сканера — без входа. */
  async previewPreset(id: number): Promise<PresetPreview> {
    const response = await this.api.get(`/presets/${id}/preview`);
    return response.data;
  }

  async deleteRoute(routeId: number, force = false): Promise<{ status: string }> {
    // force=true — удалить даже если маршрут трекает позицию (dust/фантом, которую
    // нельзя закрыть). Бэкенд по умолчанию блокирует live-маршрут с filled_usd>0.
    const response = await this.api.delete(`/routes/${routeId}`, {
      params: force ? { force: true } : undefined,
    });
    return response.data;
  }

  // Route control
  async startRoute(routeId: number): Promise<{ status: string }> {
    const response = await this.api.post(`/routes/${routeId}/start`);
    return response.data;
  }

  async stopRoute(routeId: number): Promise<{ status: string }> {
    const response = await this.api.post(`/routes/${routeId}/stop`);
    return response.data;
  }

  async flipRoute(routeId: number): Promise<{ status: string }> {
    const response = await this.api.post(`/routes/${routeId}/flip`);
    return response.data;
  }

  async openPosition(routeId: number, all = false, amount?: number): Promise<{ status: string; session_id?: string }> {
    // all=true — фоновая доливка ВСЕХ чанков с паузой cooldown_sec между ними.
    // По умолчанию (false) — один чанк за вызов. amount — явный размер доливки
    // в ЕДИНИЦАХ маршрута (токены или $ по size_mode) вместо chunk_size.
    const params: Record<string, unknown> = {};
    if (all) params.all = true;
    if (amount && amount > 0) params.amount = amount;
    const response = await this.api.post(`/routes/${routeId}/open`, null, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  }

  async closePosition(routeId: number, usd?: number, chunks?: number, delay?: number): Promise<{ status: string }> {
    // usd (опц.) — частичное закрытие: закрыть этот нотионал. Без него — всё.
    // chunks (опц., >1) — закрыть в несколько заходов; delay — пауза между ними, сек.
    const params: Record<string, number | boolean> = {};
    if (usd != null && usd > 0) params.usd = usd;
    if (chunks != null && chunks > 1) params.chunks = Math.floor(chunks);
    if (chunks != null && chunks > 1 && delay != null && delay > 0) params.delay = delay;
    const response = await this.api.post(`/routes/${routeId}/close`, null, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  }

  /** Учёт сессии = захеджированная часть позиции на биржах (меньшая нога). */
  async adoptRoute(routeId: number): Promise<{ ok: boolean; old_qty: number; new_qty: number; long_qty: number; short_qty: number; naked_qty: number }> {
    const response = await this.api.post(`/routes/${routeId}/adopt`);
    return response.data;
  }

  async pauseRoute(routeId: number): Promise<{ status: string }> {
    const response = await this.api.post(`/routes/${routeId}/pause`);
    return response.data;
  }

  /** Закрыть РЕАЛЬНУЮ биржевую позицию пары reduce-only, БЕЗ маршрута (когда бот
   *  потерял/удалил маршрут, но позиция висит на биржах). chunks>1 — частями. */
  async closePair(symbol: string, longEx: string, shortEx: string, chunks?: number, delay?: number,
                  part?: number): Promise<{ status: string }> {
    const params: Record<string, string | number> = { symbol, long_ex: longEx, short_ex: shortEx };
    if (chunks != null && chunks > 1) params.chunks = Math.floor(chunks);
    if (chunks != null && chunks > 1 && delay != null && delay > 0) params.delay = delay;
    // Доля закрытия (0..1); по умолчанию бэкенд закрывает позицию целиком.
    if (part != null && part > 0 && part < 1) params.part = part;
    const r = await this.api.post('/api/positions/close-pair', null, { params });
    return r.data;
  }

  // Settings and user management
  // Наборы интерфейса на сервере (utils/serverPrefs.ts).
  async getUiPrefs(): Promise<Record<string, { value: unknown; ts: number }>> {
    const response = await this.api.get('/api/ui-prefs');
    return response.data;
  }

  async putUiPref(key: string, value: unknown): Promise<{ ok: boolean; ts: number }> {
    const response = await this.api.put(`/api/ui-prefs/${encodeURIComponent(key)}`, { value });
    return response.data;
  }

  async getSettings(): Promise<UserSettings> {
    const response: AxiosResponse<UserSettings> = await this.api.get('/api/settings');
    return response.data;
  }

  async updateSettings(updates: Partial<UserSettings>): Promise<{ status: string }> {
    const response = await this.api.put('/api/settings', updates);
    return response.data;
  }

  async testPushover(): Promise<{ status: string }> {
    const response = await this.api.post('/api/notifications/test/pushover');
    return response.data;
  }

  /** Одноразовая ссылка t.me/<bot>?start=<token> для привязки этого user-аккаунта.
   *  Без неё бот не знает какому из пользователей привязывать chat_id. */
  async telegramBindingLink(): Promise<{ token: string; expires_ts: number; deep_link: string }> {
    const r = await this.api.post('/api/telegram/binding-token');
    return r.data;
  }

  // Credentials management
  async getCredentials(): Promise<Credential[]> {
    const response: AxiosResponse<Credential[]> = await this.api.get('/credentials');
    return response.data;
  }

  async addCredentials(credData: CredentialCreate): Promise<{ status: string }> {
    const response = await this.api.post('/credentials', credData);
    return response.data;
  }

  // Alias for ergonomics
  async addCredential(credData: CredentialCreate): Promise<{ status: string }> {
    return this.addCredentials(credData);
  }

  async deleteCredential(id: number): Promise<{ status: string }> {
    const response = await this.api.delete(`/credentials/${id}`);
    return response.data;
  }

  // Proxies — named registry, per-user. Используется как dropdown при добавлении API-ключа.
  async listProxies(): Promise<Array<{ id: number; label: string; url: string; created_at: string }>> {
    const r = await this.api.get('/proxies');
    return r.data;
  }
  async addProxy(label: string, url: string): Promise<{ status: string; id: number }> {
    const r = await this.api.post('/proxies', { label, url });
    return r.data;
  }
  async deleteProxy(id: number): Promise<{ status: string }> {
    const r = await this.api.delete(`/proxies/${id}`);
    return r.data;
  }

  // Alerts — per-user reactive rules (spread + price) + recent fires feed
  async listAlerts(): Promise<Array<{
    id: number; owner: string; kind: 'spread' | 'price' | 'deviation' | 'funding_flip' | 'net_edge' | 'funding_soon'
      | 'exit_spread' | 'funding_apr' | 'funding_interval' | 'transfer_status'; symbol: string;
    short_exchange?: string | null; long_exchange?: string | null;
    threshold_pct?: number | null;
    exchange?: string | null; price_threshold?: number | null;
    direction: 'above' | 'below';
    label?: string | null; enabled: boolean; cooldown_sec: number;
    one_shot?: boolean;
    channels?: string[] | null;
    created_at: string; last_fired_ts?: number | null;
  }>> {
    const r = await this.api.get('/api/alerts');
    return r.data;
  }
  async addAlert(body: {
    kind?: 'spread' | 'price' | 'deviation' | 'funding_flip' | 'net_edge' | 'funding_soon'
      | 'exit_spread' | 'funding_apr' | 'funding_interval' | 'transfer_status';
    symbol: string;
    short_exchange?: string; long_exchange?: string; threshold_pct?: number;
    short_symbol?: string;   // кросс-пара: у SHORT-ноги другой инструмент
    exchange?: string; price_threshold?: number;
    direction?: 'above' | 'below';
    label?: string; cooldown_sec?: number;
    one_shot?: boolean;
    channels?: ('telegram' | 'pushover')[];
    /** 'watch' — отдельный бот точечных алертов; 'main' (дефолт) — как раньше. */
    bot?: 'main' | 'watch';
    /** Условие должно держаться столько секунд подряд. 0 — слать сразу. */
    hold_sec?: number;
  }): Promise<{ status: string; id: number }> {
    const r = await this.api.post('/api/alerts', body);
    return r.data;
  }
  async patchAlert(id: number, body: {
    enabled?: boolean; threshold_pct?: number; price_threshold?: number;
    bot?: 'main' | 'watch'; hold_sec?: number;
    direction?: 'above' | 'below';
    label?: string; cooldown_sec?: number;
    one_shot?: boolean;
    channels?: ('telegram' | 'pushover')[];
  }): Promise<{ status: string }> {
    const r = await this.api.patch(`/api/alerts/${id}`, body);
    return r.data;
  }
  async deleteAlert(id: number): Promise<{ status: string }> {
    const r = await this.api.delete(`/api/alerts/${id}`);
    return r.data;
  }
  async alertsFeed(limit = 50): Promise<{ items: Array<any> }> {
    const r = await this.api.get(`/api/alerts/feed?limit=${limit}`);
    return r.data;
  }


  async getRouteJournal(routeId: number, limit = 50, eventTypes?: string): Promise<JournalEntry[]> {
    const response: AxiosResponse<JournalEntry[]> = await this.api.get(`/routes/${routeId}/journal`, {
      params: { 
        limit, 
        ...(eventTypes && { event_types: eventTypes })
      }
    });
    return response.data;
  }

  // Connection testing
  async testConnection(exchange: string): Promise<{ status: string; message: string }> {
    const response = await this.api.post('/test-connection', { exchange });
    return response.data;
  }

  // Telegram binding
  async bindTelegram(chatId: string): Promise<{ status: string }> {
    const response = await this.api.post('/telegram/bind', { chat_id: chatId });
    return response.data;
  }

  // Utility method to get WebSocket URL
  getWebSocketUrl(): string {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // When baseURL is empty ("same origin") use the page host so the
    // dev-server / prod reverse-proxy can route /ws to the backend.
    const host = this.baseURL
      ? this.baseURL.replace(/^https?:\/\//, '')
      : window.location.host;
    return `${wsProtocol}//${host}/ws`;
  }
}

export const apiClient = new ApiClient();